import { ChangeDetectionStrategy, Component, DestroyRef, inject, Inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NotificationService } from '@app/core';
import { LearningTrailsCategoryService } from '@app/core/services/learning-trails-category.service';
import { notNull, selectedEntity, selectedEntityActive } from '@app/shared/utils';
import { SIDENAV_DATA, SIDENAV_REF, SidenavRef } from '@app/shared/modules/template/components/sidenav';
import { AppSelectors } from '@app/store';
import { AppState } from '@app/store/app.state';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { SidenavStudioService } from '../../services/sidenav-studio.service';

@Component({
  templateUrl: './sidenav-learning-studio-category.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SidenavLearningStudioCategoryComponent implements OnInit {
  private readonly _translateService = inject(TranslateService);
  private readonly store = inject<Store<AppState>>(Store);
  private readonly _notificationService = inject(NotificationService);
  private readonly _learningTrailsCategoryService = inject(LearningTrailsCategoryService);
  private readonly _sidenavStudioService = inject(SidenavStudioService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly sidenavRef = inject<SidenavRef>(SIDENAV_REF);
  public readonly data = inject(SIDENAV_DATA);
  public form: FormGroup;
  public submitted$ = new BehaviorSubject<boolean>(false);

  public ngOnInit(): void {
    this.store
      .select(AppSelectors.ActiveUser)
      .pipe(takeUntilDestroyed(this.destroyRef), notNull())
      .subscribe((user) => this.initForm(user));
    if (this.data?.id) {
      this.form.patchValue(this.data);
      if (this.data?.entidades) {
        this.form.get('entidades').setValue(selectedEntity(this.data.entidades));
      }
    }
  }

  public initForm(user: Observable<any>) {
    this.form = this.formBuilder.group({
      id: undefined,
      nome: [undefined, [Validators.required, Validators.maxLength(70), Validators.minLength(3)]],
      entidades: [selectedEntityActive(user), [Validators.required]],
    });

    this.form.controls.nome.valueChanges.subscribe(() => {
      this.form.controls.nome.markAsTouched();
      this.form.controls.nome.markAsPristine();
    });
  }

  public onClose() {
    this.sidenavRef.close();
  }

  public onSave() {
    if (this.form.valid) {
      if (this.form.value.id) {
        this.update(this.form.value);
      } else {
        this.save();
      }
    } else {
      this.form.markAllAsTouched();
    }
  }

  private save() {
    if (this.form?.valid) {
      this._learningTrailsCategoryService.postCategory(this.form.value).subscribe((resp) => {
        const category = {
          id: resp['id'],
          nome: this.form.value.nome,
          colecoes: [],
          entidades: this.form.value.entidades,
          numberCourses: 0,
        };
        this._sidenavStudioService.editOrSaveCategory(category);
        this._notificationService.success(this._translateService.instant('geral.sucesso'));
        this.sidenavRef.close();
      });
    }
  }

  private update(data) {
    if (this.form?.valid) {
      this._learningTrailsCategoryService.updateCategory(data).subscribe(() => {
        this._sidenavStudioService.editOrSaveCategory({ id: this.form.value.id, nome: this.form.value.nome });
        this._notificationService.success(this._translateService.instant('geral.sucesso'));
        this.sidenavRef.close();
      });
    }
  }
}
